import { MatrixStateBadge } from '@/components/matrix-grid/matrix-state-badge';
import { PivotPill } from '@/components/matrix-grid/pivot-pill';
import { matrixDragProps } from '@/lib/matrix-drag';
import { formatMatrixDate, matrixLinkTarget, matrixTitle } from '@/lib/matrix-grid-filters';
import {
  type MatrixGridSort,
  type SortDirection,
  matrixDisplayState,
} from '@/lib/matrix-grid-search';
import { cn } from '@/lib/utils';
import {
  type MatrixSummary,
  resolveDestinataireLabel,
  resolveDestinataireValue,
} from '@mss_creator/shared';
import { Icon } from '@pms-digital/design-system';
import { Link, useNavigate } from '@tanstack/react-router';
import type { MouseEvent, ReactNode } from 'react';

export interface MatrixListTableProps {
  matrices: readonly MatrixSummary[];
  sort: MatrixGridSort;
  dir: SortDirection;
  /** A sortable header was clicked: the active one inverts, another is picked. */
  onHeaderSort: (sort: MatrixGridSort) => void;
  renderActions: (matrix: MatrixSummary) => ReactNode;
  highlightedId?: string | null;
  /**
   * Folder names by folder id: adds the inert « Dossier » column, which
   * « Toutes les matrices » shows. `null` leaves the column out.
   */
  folderNames?: ReadonlyMap<string, string> | null;
}

type Column =
  | { label: string; sort: MatrixGridSort; align?: 'right' }
  | { label: string; sort?: undefined; align?: 'right' };

/** Exactly the toolbar's sorts are sortable; the others stay inert. */
const COLUMNS_BEFORE_FOLDER: readonly Column[] = [
  { label: 'Sujet', sort: 'sujet' },
  { label: 'État', sort: 'state' },
  { label: 'Pivot', sort: 'pivot' },
  { label: 'Destinataire' },
  { label: 'Dimensions', align: 'right' },
];

const FOLDER_COLUMN: Column = { label: 'Dossier' };

const COLUMNS_AFTER_FOLDER: readonly Column[] = [
  { label: 'Créée par' },
  { label: 'Créée le', sort: 'created' },
  { label: 'Modifiée le', sort: 'updated' },
];

/**
 * The list view of the Matrices tab (issue #16, planche 04). Every row is
 * clickable, towards the same place as its card; the sujet is also a real
 * link for the keyboard. Sortable headers mirror « Trier par ».
 */
export function MatrixListTable({
  matrices,
  sort,
  dir,
  onHeaderSort,
  renderActions,
  highlightedId = null,
  folderNames = null,
}: MatrixListTableProps) {
  const navigate = useNavigate();
  const columns = [
    ...COLUMNS_BEFORE_FOLDER,
    ...(folderNames ? [FOLDER_COLUMN] : []),
    ...COLUMNS_AFTER_FOLDER,
  ];

  function openRow(event: MouseEvent<HTMLTableRowElement>, matrix: MatrixSummary) {
    // The link and the « ⋯ » menu handle their own clicks.
    if (event.target instanceof Element && event.target.closest('a, button, [role="menu"]')) {
      return;
    }
    void navigate({ to: matrixLinkTarget(matrix), params: { matrixId: matrix.id } });
  }

  return (
    <div className="rounded-lg border-2 border-gray-100 bg-white shadow-sm">
      <table className="w-full border-separate border-spacing-0 text-small">
        <thead>
          <tr>
            {columns.map((column) => {
              const active = column.sort !== undefined && column.sort === sort;
              return (
                <th
                  key={column.label}
                  scope="col"
                  aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : undefined}
                  className={cn(
                    'whitespace-nowrap border-gray-200 border-b-2 bg-gray-50 px-3 py-2 font-semibold text-caption first:rounded-tl-lg',
                    column.align === 'right' ? 'text-right' : 'text-left',
                    active ? 'text-bleu-nuit' : 'text-gray-700',
                  )}
                >
                  {column.sort === undefined ? (
                    column.label
                  ) : (
                    <button
                      type="button"
                      onClick={() => column.sort !== undefined && onHeaderSort(column.sort)}
                      className="inline-flex items-center gap-1 font-semibold hover:text-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit"
                    >
                      {column.label}
                      {active ? (
                        dir === 'asc' ? (
                          <Icon name="arrow_upward" size={14} />
                        ) : (
                          <Icon name="arrow_downward" size={14} />
                        )
                      ) : (
                        <Icon name="unfold_more" size={14} />
                      )}
                    </button>
                  )}
                </th>
              );
            })}
            <th
              scope="col"
              className="w-11 rounded-tr-lg border-gray-200 border-b-2 bg-gray-50 px-3 py-2"
            >
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {matrices.map((matrix) => (
            <tr
              key={matrix.id}
              data-matrix-id={matrix.id}
              data-highlighted={matrix.id === highlightedId || undefined}
              {...matrixDragProps(matrix.id)}
              onClick={(event) => openRow(event, matrix)}
              className={cn(
                'group cursor-pointer text-gray-700 [&:last-child>td]:border-b-0 [&>td]:border-gray-100 [&>td]:border-b [&>td]:px-3 [&>td]:py-3 [&>td]:align-middle hover:[&>td]:bg-gray-50',
                matrix.id === highlightedId && '[&>td]:bg-bleu-ciel-tint',
              )}
            >
              <td className="min-w-[200px] max-w-[330px] font-semibold text-noir">
                <Link
                  to={matrixLinkTarget(matrix)}
                  params={{ matrixId: matrix.id }}
                  className="text-noir no-underline underline-offset-2 group-hover:text-bleu-nuit group-hover:underline"
                >
                  {matrixTitle(matrix)}
                </Link>
              </td>
              <td className="whitespace-nowrap">
                <MatrixStateBadge state={matrixDisplayState(matrix)} />
              </td>
              <td className="whitespace-nowrap">
                <PivotPill pivot={matrix.pivot} />
              </td>
              <td
                className={cn(
                  'max-w-[200px] truncate whitespace-nowrap',
                  resolveDestinataireValue(matrix) === null && 'text-gray-500',
                )}
                title={resolveDestinataireLabel(matrix)}
              >
                {resolveDestinataireLabel(matrix)}
              </td>
              <td className="text-right">{matrix.dimensionCount}</td>
              {folderNames && (
                <td className="max-w-[200px] truncate whitespace-nowrap">
                  {matrix.folderId !== null ? (folderNames.get(matrix.folderId) ?? '-') : '-'}
                </td>
              )}
              <td className="whitespace-nowrap">{matrix.createdBy?.displayName ?? '-'}</td>
              <td className="whitespace-nowrap">{formatMatrixDate(matrix.createdAt)}</td>
              <td className="whitespace-nowrap">{formatMatrixDate(matrix.updatedAt)}</td>
              <td className="text-center">{renderActions(matrix)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
